import { planPriceAtMonth } from '@crm/shared';
import { Button, Modal } from '@crm/ui';
import {
  ArrowRight,
  Calendar,
  CheckCircle,
  ChevronDown,
  Clock3,
  Globe,
  LayoutTemplate,
  Loader2,
  Menu,
  MessageCircle,
  QrCode,
  Repeat2,
  Rocket,
  Scissors,
  Smartphone,
  Sparkles,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SIGNUP_ROUTES } from '@/modules/signup';
import { type PublicPlan, type Showcase, usePublicPlans, useShowcase } from '../api';
import { WhatsAppEmulator } from '../components/whatsapp-emulator';

function formatPlanPrice(price: number) {
  return price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getPlanPrice(plan: PublicPlan, month = 1) {
  const basePrice = planPriceAtMonth(
    {
      monthlyPrice: plan.monthlyPrice,
      introPrice: plan.introPrice,
      introMonths: plan.introMonths,
    },
    month,
  );

  return basePrice;
}

function hasIntroPrice(plan: PublicPlan) {
  return plan.introPrice != null && plan.introMonths > 0;
}

function getPlanFeatures(plan: PublicPlan) {
  const features = plan.features.filter(
    (feature) => !/^Até \d+ Usuários?$|^Usuários ilimitados$/i.test(feature),
  );
  if (plan.maxUsers == null) return [...features, 'Usuários ilimitados'];

  const usuario = plan.maxUsers === 1 ? 'Usuário' : 'Usuários';
  return [...features, `Até ${plan.maxUsers} ${usuario}`];
}

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-50 transition-all ${
        scrolled ? 'bg-emerald-950/95 shadow-lg backdrop-blur' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-green-400 to-emerald-600">
            <Scissors className="h-4 w-4 text-white" />
          </div>
          <span className="text-lg font-bold text-white">SalãoBot</span>
        </div>

        <div className="hidden items-center gap-7 md:flex">
          <a href="#funcionalidades" className="text-sm text-emerald-100/80 hover:text-white">
            Funcionalidades
          </a>
          <a href="#sua-pagina" className="text-sm text-emerald-100/80 hover:text-white">
            Seu Site
          </a>
          <a href="#como-funciona" className="text-sm text-emerald-100/80 hover:text-white">
            Como funciona
          </a>
          <a href="#planos" className="text-sm text-emerald-100/80 hover:text-white">
            Planos
          </a>
          <a href="#faq" className="text-sm text-emerald-100/80 hover:text-white">
            FAQ
          </a>
        </div>

        <a
          href="#planos"
          className="hidden items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-400 md:flex"
        >
          <MessageCircle className="h-4 w-4" />
          Conheça os planos
        </a>

        <button
          type="button"
          className="text-emerald-100 md:hidden"
          onClick={() => setMobileOpen((value) => !value)}
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-emerald-900 bg-emerald-950 px-4 pb-4 md:hidden">
          <a href="#funcionalidades" className="block py-3 text-sm text-emerald-100">
            Funcionalidades
          </a>
          <a href="#sua-pagina" className="block py-3 text-sm text-emerald-100">
            Seu Site
          </a>
          <a href="#como-funciona" className="block py-3 text-sm text-emerald-100">
            Como funciona
          </a>
          <a href="#planos" className="block py-3 text-sm text-emerald-100">
            Planos
          </a>
          <a href="#faq" className="block py-3 text-sm text-emerald-100">
            FAQ
          </a>
        </div>
      )}
    </nav>
  );
}

function Hero({ showcase }: { showcase: Showcase | undefined }) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-gray-950 via-[#081a14] to-gray-950 pb-20 pt-28 sm:pt-32">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(900px_420px_at_20%_0%,rgba(74,222,128,0.14),transparent),radial-gradient(760px_360px_at_85%_90%,rgba(16,185,129,0.12),transparent)]" />
        <div className="absolute left-1/4 top-0 h-[480px] w-[480px] rounded-full bg-green-500/10 blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 h-[420px] w-[420px] rounded-full bg-emerald-600/10 blur-[100px]" />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(167,243,208,0.6) 1px, transparent 0)',
            backgroundSize: '22px 22px',
          }}
        />
      </div>

      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-12 lg:px-8">
        <div className="relative">
          <div className="pointer-events-none absolute -left-4 top-2 hidden h-40 w-px bg-gradient-to-b from-transparent via-green-400/60 to-transparent lg:block" />
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-green-500/25 bg-green-500/10 px-4 py-1.5 text-base text-green-300 sm:text-lg">
            <Scissors className="h-3.5 w-3.5" />
            Sistema de Gestão para Área da Beleza
          </div>
          <h1 className="leading-tight text-white">
            <span className="block text-3xl font-extrabold sm:text-5xl lg:whitespace-nowrap lg:text-[3.5rem]">
              CRM com WhatsApp Bot
            </span>
            <span className="mt-3 block whitespace-nowrap text-xl font-semibold text-emerald-100/90 sm:mt-4 sm:text-3xl lg:text-[1.75rem]">
              Alavanque Seu Negócio com Automação e Marketing
            </span>
          </h1>
          <p className="mt-7 max-w-xl text-base text-emerald-100/70 sm:text-lg">
            Automatize agendamentos, follow-up e campanhas de marketing e deixe algumas operações do
            seu negócio no piloto automático.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <a
              href="#planos"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-500 px-6 py-3.5 text-base font-bold text-white shadow-[0_14px_32px_-18px_rgba(34,197,94,0.75)] transition hover:bg-green-400 sm:px-7 sm:py-4 sm:text-lg"
            >
              <MessageCircle className="h-5 w-5" />
              Conheça os planos
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#funcionalidades"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-700 px-6 py-3.5 text-base font-semibold text-emerald-100 transition hover:border-emerald-500 hover:text-white sm:px-7 sm:py-4 sm:text-lg"
            >
              Ver como funciona
              <ChevronDown className="h-4 w-4" />
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-sm text-emerald-100/60">
            {['Sem fidelidade', 'Configure em minutos', 'Tenha um site em minutos'].map((item) => (
              <span key={item} className="inline-flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-green-500" />
                {item}
              </span>
            ))}
          </div>
        </div>

        <div className="mx-auto h-[540px] w-full max-w-[360px] overflow-visible pb-2 sm:h-[620px] sm:max-w-[390px] lg:ml-auto lg:mr-0">
          <div className="relative h-full w-full overflow-hidden rounded-[42px] shadow-[0_28px_80px_-34px_rgba(16,185,129,0.7)]">
            <WhatsAppEmulator showcase={showcase} />
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  const items = [
    {
      icon: <MessageCircle className="h-6 w-6" />,
      title: 'CRM para Gestão',
      text: 'Respostas automáticas para dúvidas frequentes, mesmo fora do horário comercial. Tudo registrado por cliente: serviços, conversas e preferências para vender melhor.',
      card: 'from-teal-500/10 via-emerald-500/5 to-transparent border-teal-400/20',
      iconWrap: 'bg-teal-500/12 text-teal-300',
    },
    {
      icon: <Calendar className="h-6 w-6" />,
      title: 'WhatsApp Bot',
      text: 'Cliente escolhe serviço, dia, horário e profissional direto no WhatsApp — sem você precisar responder. Clientes que recebem confirmação e lembrete faltam menos. Sua agenda fecha o dia mais cheia.',
      card: 'from-sky-500/10 via-cyan-500/5 to-transparent border-sky-400/20',
      iconWrap: 'bg-sky-500/12 text-sky-300',
    },
    {
      icon: <Repeat2 className="h-6 w-6" />,
      title: 'WhatsApp Marketing',
      text: 'Envie campanhas segmentadas por WhatsApp e e-mail para os clientes certos, no momento certo. Traga de volta clientes que sumiram com uma mensagem no momento certo, sem depender de memória.',
      card: 'from-violet-500/10 via-indigo-500/5 to-transparent border-violet-400/20',
      iconWrap: 'bg-violet-500/12 text-violet-300',
    },
  ];

  return (
    <section id="funcionalidades" className="bg-gray-900 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            Principais funcionalidades
          </h2>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <div
              key={item.title}
              className={`group rounded-2xl border bg-gradient-to-br p-6 shadow-[0_10px_30px_-20px_rgba(16,185,129,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_34px_-22px_rgba(16,185,129,0.45)] ${item.card}`}
            >
              <div
                className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl ${item.iconWrap} ring-1 ring-white/10 transition-transform duration-300 group-hover:scale-105`}
              >
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-white">{item.title}</h3>
              <p className="mt-2 text-sm text-emerald-100/70">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LandingPageSection() {
  const benefits = [
    {
      icon: <Globe className="h-5 w-5" />,
      title: 'Presença Web Profissional',
      text: 'Serviços, profissionais, preços e botão de agendamento reunidos num único site. Tudo que o cliente precisa ver para decidir e marcar na hora.',
    },
    {
      icon: <LayoutTemplate className="h-5 w-5" />,
      title: 'Múltiplos layouts prontos',
      text: 'Escolha entre múltiplos layouts pré-configurados e ajuste cores, textos e serviços. Sem precisar de designer ou agência.',
    },
    {
      icon: <Smartphone className="h-5 w-5" />,
      title: 'Integrado com WhatsApp Bot e WhatsApp Marketing',
      text: 'Lead chegou pelo site? O bot já assume: responde, qualifica e agenda automaticamente. Sem precisar de ninguém do outro lado.',
    },
  ];

  return (
    <section id="sua-pagina" className="relative overflow-hidden bg-gray-950 py-20">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(900px_400px_at_50%_50%,rgba(74,222,128,0.09),transparent)]" />
        <div className="absolute left-0 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-emerald-500/8 blur-3xl" />
        <div className="absolute right-0 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-teal-500/8 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(134,239,172,0.55) 1px, transparent 0)',
            backgroundSize: '20px 20px',
          }}
        />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-green-500/25 bg-green-500/10 px-4 py-1.5 text-sm text-green-300">
            <LayoutTemplate className="h-3.5 w-3.5" />
            Tenha presença web em minutos
          </div>
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            Landing Page Pronta e Personalizável
          </h2>
          <p className="mt-3 text-lg text-emerald-100/70">
            Landing page pré-configurada integrada — recursos prontos e totalmente customizáveis,
            sem precisar de agência ou código.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-3">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className="group rounded-2xl border border-emerald-400/15 bg-gradient-to-br from-emerald-500/8 to-transparent p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-400/30"
            >
              <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/12 text-emerald-300 ring-1 ring-white/10 transition-transform duration-300 group-hover:scale-105">
                {benefit.icon}
              </div>
              <h3 className="text-base font-semibold text-white">{benefit.title}</h3>
              <p className="mt-1.5 text-sm text-emerald-100/65">{benefit.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            href="#planos"
            className="inline-flex items-center gap-2 rounded-xl bg-green-500 px-6 py-3 text-sm font-bold text-white shadow-[0_10px_28px_-12px_rgba(34,197,94,0.6)] transition hover:bg-green-400 sm:px-7 sm:py-3.5 sm:text-base"
          >
            <MessageCircle className="h-4 w-4" />
            Conheça os planos
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      icon: <Sparkles className="h-6 w-6" />,
      title: 'Cadastre seus serviços',
      description:
        'Cadastre serviços, duração e profissionais em poucos cliques, com configuração simples.',
      highlight: 'Configuração simplificada',
    },
    {
      icon: <QrCode className="h-6 w-6" />,
      title: 'Configure o WhatsApp',
      description: 'Leia o QR Code e conecte seu número em menos de 2 minutos.',
      highlight: 'Setup inicial rápido',
    },
    {
      icon: <Rocket className="h-6 w-6" />,
      title: 'Comece a receber leads',
      description:
        'Seu estabelecimento ganha uma página web para divulgar nas redes sociais e receber leads.',
      highlight: 'Sua vitrine online',
    },
  ] as const;

  return (
    <section id="como-funciona" className="relative overflow-hidden bg-gray-950 py-20">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_500px_at_50%_-20%,rgba(74,222,128,0.12),transparent),radial-gradient(800px_360px_at_8%_20%,rgba(16,185,129,0.1),transparent),radial-gradient(900px_420px_at_92%_85%,rgba(20,184,166,0.1),transparent)]" />
        <div className="absolute -left-28 top-2 h-80 w-80 rounded-full bg-green-400/8 blur-3xl" />
        <div className="absolute right-[30%] top-10 h-60 w-60 rounded-full bg-emerald-400/8 blur-3xl" />
        <div className="absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-teal-400/8 blur-3xl" />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/12 via-transparent to-gray-950/70" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(134,239,172,0.55) 1px, transparent 0)',
            backgroundSize: '20px 20px',
          }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(16,185,129,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(16,185,129,0.08)_1px,transparent_1px)] bg-[size:72px_72px] opacity-[0.04]" />
      </div>

      <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            Configure rápido e comece a vender em minutos
          </h2>
          <p className="mt-3 text-lg text-emerald-100/70">
            Sem implantação longa, sem equipe técnica e sem complicação.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-green-500/25 bg-green-500/10 px-4 py-1.5 text-sm text-green-300">
            <Clock3 className="h-4 w-4" />
            Do zero ao primeiro agendamento em poucos minutos
          </div>
        </div>

        <div className="relative grid gap-5 md:grid-cols-3">
          <div className="pointer-events-none absolute left-[18%] right-[18%] top-[98px] z-0 hidden h-0.5 bg-gradient-to-r from-transparent via-green-400/45 to-transparent md:block" />
          {steps.map((step, index) => (
            <div
              key={step.title}
              className="relative z-10 rounded-2xl bg-transparent p-6 text-center transition hover:-translate-y-0.5"
            >
              <span className="mb-3 inline-block text-3xl font-extrabold text-green-400/90">
                {String(index + 1).padStart(2, '0')}
              </span>
              <div className="mb-4 flex items-center justify-center">
                <span className="relative z-20 inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-green-500/25 bg-emerald-950 text-green-400 shadow-[0_0_0_4px_rgba(3,7,18,0.85)] [&_svg]:h-8 [&_svg]:w-8">
                  {step.icon}
                </span>
              </div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-green-300/70">
                {step.highlight}
              </p>
              <h3 className="text-xl font-semibold text-white sm:text-2xl">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-300/80">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const [freeModalOpen, setFreeModalOpen] = useState(false);
  const plansQuery = usePublicPlans();
  const plans = [...(plansQuery.data?.data ?? [])].sort((a, b) => a.order - b.order);

  return (
    <section id="planos" className="bg-gray-900 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">Escolha seu plano</h2>

          <div className="mx-auto mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-emerald-100/60">
            {['Teste grátis', 'Cancela quando quiser', 'Sem taxa de implantação'].map((item) => (
              <span key={item} className="inline-flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-green-500" />
                {item}
              </span>
            ))}
          </div>
        </div>

        {plansQuery.isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-green-400" />
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`flex h-full flex-col rounded-2xl border p-6 ${
                  plan.highlighted
                    ? 'border-green-500/60 bg-gradient-to-b from-green-500/15 to-emerald-900/15'
                    : 'border-gray-800 bg-gray-900'
                }`}
              >
                <h3
                  className={`text-2xl font-bold ${plan.highlighted ? 'text-green-400' : 'text-white'}`}
                >
                  {plan.name}
                </h3>
                <p className="text-sm text-gray-400">{plan.description}</p>
                <p className="mt-4 text-4xl font-extrabold text-white">
                  R${' '}
                  {formatPlanPrice(
                    hasIntroPrice(plan) ? getPlanPrice(plan, 1) : getPlanPrice(plan),
                  )}
                  <span className="text-sm text-gray-400"> / mês</span>
                </p>
                {hasIntroPrice(plan) ? (
                  <p className="whitespace-nowrap text-[13px] leading-5 tracking-tight text-gray-400">
                    nos primeiros {plan.introMonths} meses; depois R${' '}
                    {formatPlanPrice(getPlanPrice(plan, plan.introMonths + 1))}
                  </p>
                ) : null}

                <ul className="mt-5 space-y-2">
                  {getPlanFeatures(plan).map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-gray-300">
                      <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-6">
                  {plan.monthlyPrice === 0 ? (
                    <button
                      type="button"
                      onClick={() => setFreeModalOpen(true)}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold ${
                        plan.highlighted
                          ? 'bg-green-500 text-white hover:bg-green-400'
                          : 'border border-gray-700 text-white hover:border-green-500/60'
                      }`}
                    >
                      {plan.ctaLabel ?? 'Assinar'}
                    </button>
                  ) : (
                    <Link
                      to={`${SIGNUP_ROUTES.WIZARD}?plano=${encodeURIComponent(plan.id)}`}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold ${
                        plan.highlighted
                          ? 'bg-green-500 text-white hover:bg-green-400'
                          : 'border border-gray-700 text-white hover:border-green-500/60'
                      }`}
                    >
                      {plan.ctaLabel ?? 'Assinar'}
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-6 text-center text-xs text-gray-500">
          Créditos são consumidos por mensagens enviadas em campanhas e pelo WhatsApp Bot. A
          quantidade de mensagens por crédito fica disponível no seu painel e depende dos valores
          cobrados pela Meta e empresas intermediadoras.
        </p>
        {freeModalOpen ? (
          <Modal open onClose={() => setFreeModalOpen(false)} title="Plano Free" size="medium">
            <p className="text-base leading-relaxed text-muted-foreground">
              Este plano não inclui o WhatsApp Bot nem WhatsApp marketing. Se isso for importante
              para você, escolha outro plano pago, sem fidelidade, e cancele quando quiser.
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                className="w-full whitespace-nowrap sm:w-auto"
                onClick={() => setFreeModalOpen(false)}
              >
                Ver planos com WhatsApp
              </Button>
              <Link
                to={`${SIGNUP_ROUTES.WIZARD}?plano=${encodeURIComponent(plans[0]?.id ?? '')}`}
                className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground sm:w-auto"
                onClick={() => setFreeModalOpen(false)}
              >
                Quero grátis mesmo
              </Link>
            </div>
          </Modal>
        ) : null}
      </div>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  const faqs = [
    ['Precisa de cartão para testar?', 'Não. Você pode iniciar o teste sem cartão de crédito.'],
    [
      'Quanto tempo leva para configurar?',
      'Você conecta o WhatsApp e ativa o menu em poucos minutos.',
    ],
    [
      'O bot substitui minha recepcionista?',
      'Ele automatiza o repetitivo: triagem, agendamento, confirmação e lembretes. Sua equipe foca no atendimento humano.',
    ],
    [
      'Posso usar com vários profissionais?',
      'Sim. O fluxo contempla escolha de serviço, horário e profissional no WhatsApp.',
    ],
    [
      'Para quais tipos de negócio funciona?',
      'Salões de beleza, barbearias, espaços de estética e qualquer negócio do setor de beleza que trabalhe com agendamento.',
    ],
    [
      'Posso cancelar quando quiser?',
      'Sim. Cancelamento simples, sem burocracia, direto pelo seu atendimento.',
    ],
    [
      'O sistema é para negócios de qual tamanho?',
      'Funciona para autônomos, pequenos e médios negócios. Foi desenhado para ser enxuto e acessível — sem as complexidades e o custo de sistemas para grandes redes.',
    ],
  ] as const;

  return (
    <section id="faq" className="bg-gray-950 py-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2 className="mb-8 text-center text-3xl font-extrabold text-white">
          Perguntas frequentes
        </h2>
        <div className="space-y-3">
          {faqs.map(([question, answer], index) => (
            <div
              key={question}
              className="overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] shadow-[0_10px_28px_-24px_rgba(16,185,129,0.55)] transition-colors duration-300"
            >
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left text-white transition-colors hover:bg-white/[0.02]"
                onClick={() => setOpen(open === index ? null : index)}
                aria-expanded={open === index}
              >
                <span className="text-sm font-semibold text-white sm:text-base">{question}</span>
                <ChevronDown
                  className={`h-4 w-4 flex-shrink-0 text-emerald-300 transition-transform duration-300 ${
                    open === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {open === index && (
                <div className="border-t border-white/10 bg-emerald-500/[0.04]">
                  <p className="px-5 py-4 text-sm leading-relaxed text-emerald-50/95">{answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="relative overflow-hidden bg-gray-900 py-20">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-16 top-10 h-56 w-56 rounded-full bg-green-500/15 blur-3xl" />
        <div className="absolute -right-20 bottom-0 h-64 w-64 rounded-full bg-emerald-500/12 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(134,239,172,0.55) 1px, transparent 0)',
            backgroundSize: '24px 24px',
          }}
        />
      </div>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-green-500/25 bg-gradient-to-br from-green-500/12 via-emerald-900/25 to-emerald-950/35 p-7 text-center sm:p-12">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-1/2 top-0 h-40 w-[28rem] -translate-x-1/2 bg-gradient-to-r from-transparent via-green-400/20 to-transparent blur-2xl" />
            <div className="absolute -left-16 top-10 h-44 w-44 rounded-full bg-green-400/15 blur-3xl" />
            <div className="absolute -right-16 bottom-8 h-52 w-52 rounded-full bg-emerald-400/15 blur-3xl" />
            <div
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 1px 1px, rgba(167,243,208,0.7) 1px, transparent 0)',
                backgroundSize: '20px 20px',
              }}
            />
          </div>

          <div className="relative z-10">
            <div className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-green-500/25 bg-green-500/10 px-4 py-1.5 text-sm text-green-300">
              <MessageCircle className="h-4 w-4" />
              Pronto para levar seu negócio ao próximo nível?
            </div>
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              Comece já e faça seu negócio faturar mais
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-emerald-100/75">
              Com CRM para gestão, WhatsApp Bot, WhatsApp Marketing e Landing Page você poderá
              automatizar seus atendimentos e vender muito mais.
            </p>

            <a
              href="#planos"
              className="mt-7 inline-flex items-center gap-2 rounded-xl bg-green-500 px-6 py-3 text-sm font-bold text-white transition hover:bg-green-400 sm:px-7 sm:py-3.5 sm:text-base"
            >
              <MessageCircle className="h-4 w-4" />
              Conheça os planos
              <ArrowRight className="h-4 w-4" />
            </a>

            <p className="mt-3 text-xs text-emerald-100/60">
              Plano grátis disponível • Cancelamento simples
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-800 bg-gray-950 py-8">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 text-center sm:flex-row sm:px-6 sm:text-left lg:px-8">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-green-400 to-emerald-600">
            <Scissors className="h-4 w-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-white">SalãoBot</span>
        </div>
        <p className="text-xs text-gray-400">CRM + WhatsApp Bot para pequenos e médios salões.</p>
        <p className="text-xs text-gray-500">© {new Date().getFullYear()} SalãoBot</p>
      </div>
    </footer>
  );
}

export function SalonLandingPage() {
  const showcase = useShowcase();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen font-sans">
      <Navbar />
      <Hero showcase={showcase.data} />
      <Features />
      <LandingPageSection />
      <HowItWorks />
      <Pricing />
      <Faq />
      <FinalCta />
      <Footer />
    </div>
  );
}
